import {
  PASTA_STATUS_LABELS,
  apiStatusToPastaStatus,
} from '../../../shared/habitaStatusPresentation';
import type {
  CampoExtraidoStatus,
  DocumentoCategoria,
  DocumentoStatus,
  HabitaCampoExtraido,
  HabitaCliente,
  HabitaCustomerDossier,
  HabitaDossierField,
  HabitaDossierFieldSource,
  HabitaDocumento,
  HabitaProcessDetail,
  ProcessFileModel,
} from '../../../shared/models';
import {
  UNKNOWN_DOCUMENT_SUBTYPE_LABEL,
  formatProcessDocumentSubtypeLabel,
  formatProcessDocumentTypeLabel,
  formatCEP,
  formatCPF,
  formatHabitaCurrency,
  formatHabitaNullableDate,
} from '../../../shared/utils';

function firstString(...values: unknown[]): string {
  const value = values.find((item) => item !== null && item !== undefined && String(item).trim() !== '');
  return value === undefined || value === null ? '-' : String(value);
}

function optionalString(value: unknown): string | null {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  return String(value);
}

function objectRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function toConfidencePercentage(...values: unknown[]): number {
  const value = values
    .map(toNumberOrNull)
    .find((item): item is number => item !== null);
  if (value === undefined) return 0;

  const percentage = value >= 0 && value <= 1 ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(percentage)));
}

function toConfidencePercentageOrNull(...values: unknown[]): number | null {
  const value = values
    .map(toNumberOrNull)
    .find((item): item is number => item !== null);
  if (value === undefined) return null;

  const percentage = value >= 0 && value <= 1 ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(percentage)));
}

function statusFromConfidence(confidence: number | null): CampoExtraidoStatus {
  if (confidence === null || confidence <= 0) return 'sem_validacao';
  return confidence < 75 ? 'revisao' : 'validado';
}

function normalizeDateTime(value: unknown): string {
  if (!value) return '';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function normalizeDocType(value: unknown): string {
  return normalizeStatus(value);
}

function processFileDocType(file: ProcessFileModel): unknown {
  return file.doc_type;
}

function mapProcessFileCategory(file: ProcessFileModel): DocumentoCategoria {
  const docType = normalizeDocType(processFileDocType(file));
  if (docType === 'IDENTIFICATION' || docType === 'IDENTIFICACAO') return 'identificacao';
  if (docType === 'INCOME_PROOF' || docType === 'RENDA') return 'renda';
  if (docType === 'RESIDENCE_PROOF' || docType === 'RESIDENCIA') return 'residencia';
  if (docType === 'ESTADO_CIVIL') return 'estado_civil';
  return 'outro';
}

function mapProcessFileStatus(file: ProcessFileModel): DocumentoStatus {
  const fileStatus = normalizeDocType(file.status);
  if (fileStatus === 'EXTRACTION_ERROR') return 'reprovado';
  if (fileStatus === 'EXTRACTION_OK') return 'validado';
  if (fileStatus === 'AI_PROCESSING') return 'em_analise';
  if (fileStatus === 'RECEIVED') return 'recebido';

  const status = normalizeDocType(file.ai_status);
  if (status.includes('REJECT') || status.includes('REPROV') || status.includes('FAILED') || status.includes('INVALID')) {
    return 'reprovado';
  }
  if (status.includes('PROCESS')) return 'em_analise';
  if (status.includes('PENDING')) return 'recebido';
  if (status.includes('COMPLETED')) return 'validado';
  return 'desconhecido';
}

function fileNameFromPath(path: unknown): string {
  const value = String(path ?? '').trim().split(/[?#]/)[0];
  if (!value) return 'Documento';
  return value.split('/').filter(Boolean).pop() ?? value;
}

const fieldLabels: Record<string, string> = {
  address: 'Endereço',
  address_line_1: 'Logradouro',
  address_line_2: 'Complemento',
  avg_monthly_income: 'Renda média mensal',
  birth_date: 'Nascimento',
  birth_place: 'Naturalidade',
  birthplace: 'Naturalidade',
  cep: 'CEP',
  city: 'Cidade',
  considered_total_income: 'Total considerado',
  cpf: 'CPF',
  cpf_cnpj: 'CPF/CNPJ',
  cpf_number: 'CPF',
  data_admissao: 'Data de admissão',
  district: 'Bairro',
  document_type: 'Tipo do documento',
  expiration_date: 'Validade',
  father_full_name: 'Nome do genitor 1',
  full_address: 'Endereço completo',
  full_name: 'Nome completo',
  gender: 'Gênero',
  gross_income: 'Renda bruta',
  hire_date: 'Data de admissão',
  income_proof_type: 'Tipo de comprovante',
  income_source_cpf_cnpj: 'CPF/CNPJ da fonte pagadora',
  income_source_name: 'Fonte pagadora',
  income_source_type: 'Tipo da fonte pagadora',
  issue_date: 'Emissão',
  mother_full_name: 'Nome do genitor 2',
  nationality: 'Nacionalidade',
  net_income: 'Renda líquida',
  nome_titular: 'Nome do titular',
  not_considered_total_income: 'Total não considerado',
  number: 'Número',
  number_transactions: 'Quantidade de transações',
  occupation: 'Profissão',
  parents: 'Genitores',
  pis_pasep_nis: 'PIS/PASEP/NIS',
  property_regime: 'Regime de bens',
  reference_period: 'Período de referência',
  rg_number: 'RG',
  state: 'Estado',
  zipcode: 'CEP',
};

const dossierGroupLabels: Record<string, string> = {
  civil_state_data: 'Estado civil',
  identification_data: 'Identificação',
  income_data: 'Renda',
  residence_data: 'Residência',
};

const dossierCollectionLabels: Record<string, string> = {
  incomes: 'Comprovação',
  monthly_incomes: 'Renda mensal',
};

const ignoredDossierValueKeys = new Set([
  'file_id',
]);

const ignoredExtractionKeys = new Set([
  'confidence_score',
  'confidence_scores',
  'document_type',
  'extraction_extra',
  'filename',
  'metadata',
  'processed_text',
  'processing_config',
  'processing_finish',
  'processing_start',
  'processing_time',
  'raw_text',
  'type',
  'user_id',
]);

function labelFromKey(key: string): string {
  const cleanKey = key.replace(/^(identity_data|metadata|address_data|income_data|residence_data|civil_state_data|identification_data)_/, '');
  return fieldLabels[cleanKey] ?? cleanKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toLocaleUpperCase('pt-BR'));
}

function isParentageKey(key: string): boolean {
  const normalized = normalizeDocType(key);
  const cleanKey = normalized.replace(/^(IDENTITY_DATA|IDENTIFICACAO_DATA|IDENTIFICATION_DATA)_/, '');
  return ['FILIACAO', 'FILIATION', 'PARENTAGE', 'PARENTS'].includes(cleanKey);
}

function genitorLabel(index: number): string {
  return `Nome do genitor ${index + 1}`;
}

function stringifyExtractedValue(value: unknown): string {
  if (value === null || value === undefined || String(value).trim() === '') return '-';
  if (Array.isArray(value)) return value.map(stringifyExtractedValue).filter((item) => item !== '-').join(', ') || '-';
  if (typeof value === 'object') {
    const objectValue = value as Record<string, unknown>;
    if ('city' in objectValue || 'state' in objectValue || 'country' in objectValue) {
      return [objectValue.city, objectValue.state, objectValue.country]
        .filter((item) => item !== null && item !== undefined && String(item).trim() !== '')
        .join(' / ') || '-';
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function unwrapExtractedFieldValue(value: unknown): unknown {
  const record = objectRecord(value);
  if (record && Object.prototype.hasOwnProperty.call(record, 'value')) return record.value;
  return value;
}

function isSimpleExtractedValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (['string', 'number', 'boolean'].includes(typeof value)) return true;
  return Array.isArray(value) && value.every((item) => (
    item === null ||
    item === undefined ||
    ['string', 'number', 'boolean'].includes(typeof item)
  ));
}

function splitParentageValues(value: unknown): string[] {
  const rawValue = unwrapExtractedFieldValue(value);
  if (Array.isArray(rawValue)) {
    return rawValue.map(stringifyExtractedValue).filter((item) => item !== '-');
  }

  const singleValue = stringifyExtractedValue(rawValue);
  return singleValue === '-' ? [] : [singleValue];
}

function readParentageFieldCandidates(key: string, value: unknown, documento: string): HabitaCampoExtraido[] {
  if (!isParentageKey(key)) return [];

  const values = splitParentageValues(value);
  const field = objectRecord(value);
  const confidence = toConfidencePercentageOrNull(field?.confidence);
  const statusValue = normalizeDocType(field?.status);
  const status: CampoExtraidoStatus =
    statusValue === 'ABSENT' || statusValue === 'UNDETERMINED'
      ? 'revisao'
      : statusFromConfidence(confidence);

  return values.slice(0, 2).map((valor, index) => ({
    label: genitorLabel(index),
    valor,
    origem: 'OCR',
    documento,
    confianca: confidence,
    status,
  }));
}

function readSimpleFieldCandidate(key: string, value: unknown, documento: string): HabitaCampoExtraido | null {
  if (!isSimpleExtractedValue(value)) return null;

  const valor = stringifyExtractedValue(value);
  if (valor === '-') return null;

  return {
    label: labelFromKey(key),
    valor,
    origem: 'OCR',
    documento,
    confianca: null,
    status: 'sem_validacao',
  };
}

function readFieldCandidate(key: string, rawValue: unknown, documento: string): HabitaCampoExtraido | null {
  if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) {
    return null;
  }

  const field = rawValue as { value?: unknown; confidence?: unknown; status?: unknown };
  if (!('value' in field)) return null;

  const valor = stringifyExtractedValue(field.value);
  if (valor === '-') return null;

  const confidence = toConfidencePercentageOrNull(field.confidence);
  const statusValue = normalizeDocType(field.status);
  const status: CampoExtraidoStatus =
    statusValue === 'ABSENT' || statusValue === 'UNDETERMINED'
      ? 'revisao'
      : statusFromConfidence(confidence);

  return {
    label: labelFromKey(key),
    valor,
    origem: 'OCR',
    documento,
    confianca: confidence,
    status,
  };
}

function collectExtractedFields(source: unknown, documento: string, prefix = ''): HabitaCampoExtraido[] {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return [];

  const fields: HabitaCampoExtraido[] = [];
  Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
    if (ignoredExtractionKeys.has(key)) return;

    const fieldKey = prefix ? `${prefix}_${key}` : key;
    const parentageCandidates = readParentageFieldCandidates(fieldKey, value, documento);
    if (parentageCandidates.length > 0) {
      fields.push(...parentageCandidates);
      return;
    }

    const candidate = readFieldCandidate(fieldKey, value, documento);
    if (candidate) {
      fields.push(candidate);
      return;
    }

    const simpleCandidate = readSimpleFieldCandidate(fieldKey, value, documento);
    if (simpleCandidate) {
      fields.push(simpleCandidate);
      return;
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      fields.push(...collectExtractedFields(value, documento, fieldKey));
    }
  });

  return fields;
}

function sourceOriginFromDossier(value: unknown): HabitaCampoExtraido['origem'] {
  const normalized = optionalString(value)?.toLowerCase();
  if (normalized === 'ocr') return 'OCR';
  if (normalized === 'manual') return 'Manual';
  if (normalized === 'ia') return 'IA';
  if (normalized === 'chavinha') return 'Chavinha';
  return 'OCR';
}

function statusFromDossierConfidence(value: unknown): CampoExtraidoStatus {
  return statusFromConfidence(toConfidencePercentageOrNull(value));
}

function labelFromDossierPath(path: string[]): string {
  const [groupKey, ...fieldPath] = path;

  if (fieldPath.length === 0) {
    return labelFromKey(groupKey);
  }

  const groupLabel = dossierGroupLabels[groupKey];
  const labelParts: string[] = [];

  fieldPath.forEach((part, index) => {
    const collectionLabel = dossierCollectionLabels[part];
    if (collectionLabel) {
      const nextPart = fieldPath[index + 1];
      const nextIndex = Number(nextPart);
      labelParts.push(Number.isInteger(nextIndex) ? `${collectionLabel} ${nextIndex + 1}` : collectionLabel);
      return;
    }

    if (/^\d+$/.test(part)) return;

    labelParts.push(labelFromKey(part));
  });

  const label = labelParts.filter(Boolean).join(' · ');
  return [groupLabel, label].filter(Boolean).join(' · ') || labelFromKey(path.join('_'));
}

function isPrimitiveDossierValue(value: unknown): boolean {
  return value === null || value === undefined || ['string', 'number', 'boolean'].includes(typeof value);
}

function hasDisplayDossierValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some(hasDisplayDossierValue);
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).some(hasDisplayDossierValue);
  return String(value).trim() !== '';
}

function collectDossierValueFields(
  value: unknown,
  path: string[],
  meta: {
    confidence: number | null;
    docId: string;
    documento: string;
    origem: HabitaCampoExtraido['origem'];
    sourceDetail: Record<string, unknown> | null;
    status: CampoExtraidoStatus;
  }
): HabitaCampoExtraido[] {
  if (!hasDisplayDossierValue(value)) return [];

  if (isPrimitiveDossierValue(value)) {
    return [{
      label: labelFromDossierPath(path),
      valor: stringifyExtractedValue(value),
      origem: meta.origem,
      documento: meta.documento,
      docId: meta.docId,
      sourceDetail: meta.sourceDetail,
      confianca: meta.confidence,
      status: meta.status,
    }];
  }

  if (Array.isArray(value)) {
    const hasObjectItems = value.some((item) => item && typeof item === 'object');
    const currentKey = path[path.length - 1] ?? '';
    if (!hasObjectItems && isParentageKey(currentKey)) {
      return value.slice(0, 2).flatMap((item, index) => {
        const valor = stringifyExtractedValue(item);
        if (valor === '-') return [];

        return [{
          label: genitorLabel(index),
          valor,
          origem: meta.origem,
          documento: meta.documento,
          docId: meta.docId,
          sourceDetail: meta.sourceDetail,
          confianca: meta.confidence,
          status: meta.status,
        }];
      });
    }

    if (!hasObjectItems) {
      return [{
        label: labelFromDossierPath(path),
        valor: stringifyExtractedValue(value),
        origem: meta.origem,
        documento: meta.documento,
        docId: meta.docId,
        sourceDetail: meta.sourceDetail,
        confianca: meta.confidence,
        status: meta.status,
      }];
    }

    return value.flatMap((item, index) => (
      collectDossierValueFields(item, [...path, String(index)], meta)
    ));
  }

  return Object.entries(value as Record<string, unknown>).flatMap(([key, nestedValue]) => {
    if (ignoredDossierValueKeys.has(key)) return [];
    return collectDossierValueFields(nestedValue, [...path, key], meta);
  });
}

function collectDossierFieldsByFileId(dossierValue: unknown): Map<string, HabitaCampoExtraido[]> {
  const dossier = objectRecord(dossierValue);
  const fieldsByFileId = new Map<string, HabitaCampoExtraido[]>();
  if (!dossier) return fieldsByFileId;

  Object.entries(dossier).forEach(([key, rawSource]) => {
    const source = objectRecord(rawSource);
    if (!source || !('value' in source)) return;

    const sourceDetail = objectRecord(source.source_detail);
    const fileId = optionalString(sourceDetail?.file_id);
    if (!fileId) return;

    const documento = optionalString(sourceDetail?.source_name) ?? labelFromKey(key);
    const meta = {
      confidence: toConfidencePercentageOrNull(source.confidence),
      docId: fileId,
      documento,
      origem: sourceOriginFromDossier(source.source),
      sourceDetail,
      status: statusFromDossierConfidence(source.confidence),
    };
    const fields = collectDossierValueFields(source.value, [key], meta);
    if (fields.length === 0) return;

    fieldsByFileId.set(fileId, [
      ...(fieldsByFileId.get(fileId) ?? []),
      ...fields,
    ]);
  });

  return fieldsByFileId;
}

function mergeExtractedFields(...groups: HabitaCampoExtraido[][]): HabitaCampoExtraido[] {
  const seen = new Set<string>();
  const fields: HabitaCampoExtraido[] = [];

  groups.flat().forEach((field) => {
    const key = `${field.label}::${field.valor}::${field.docId ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    fields.push(field);
  });

  return fields;
}

function validationDataMessage(value: unknown): string | null {
  const record = objectRecord(value);
  if (!record) return null;

  const directMessage = optionalString(record.message) ?? optionalString(record.error);
  if (directMessage) return directMessage;

  for (const [key, nestedValue] of Object.entries(record)) {
    const normalizedKey = normalizeDocType(key);
    if (normalizedKey === 'MESSAGE' || normalizedKey === 'ERROR' || normalizedKey === 'JUSTIFICATIVA') {
      const message = optionalString(nestedValue);
      if (message) return message;
    }

    const nestedRecord = objectRecord(nestedValue);
    const nestedMessage = optionalString(nestedRecord?.message) ?? optionalString(nestedRecord?.error);
    if (nestedMessage) return nestedMessage;
  }

  return null;
}

function buildRejectionReason(file: ProcessFileModel): string | undefined {
  const validationMessages = Array.from(
    new Set([
      validationDataMessage(file.validation_data),
      validationDataMessage(file.rule_check_data),
    ].filter((message): message is string => Boolean(message)))
  );
  if (validationMessages.length > 0) return validationMessages.join('; ');

  return file.ai_diagnosis?.justificativa ?? undefined;
}

function getProcessFileTypeLabel(file: ProcessFileModel): string {
  return formatProcessDocumentTypeLabel(processFileDocType(file));
}

function getProcessFileSubtypeLabel(file: ProcessFileModel): string {
  return formatProcessDocumentSubtypeLabel(file.doc_subtype, file.doc_subtype_text);
}

function getProcessFileNameLabel(file: ProcessFileModel, typeLabel: string): string {
  const subtypeLabel = getProcessFileSubtypeLabel(file);
  return subtypeLabel === UNKNOWN_DOCUMENT_SUBTYPE_LABEL ? typeLabel : subtypeLabel;
}

function mapProcessFileToHabitaDocumento(file: ProcessFileModel): HabitaDocumento {
  const extractedDataRecord = objectRecord(file.extracted_data) ?? {};
  const normalizedFile: ProcessFileModel = {
    ...file,
    extracted_data: (Object.keys(extractedDataRecord).length > 0 ? extractedDataRecord : null) as ProcessFileModel['extracted_data'],
    doc_type: firstString(file.doc_type),
    doc_subtype: optionalString(file.doc_subtype),
    doc_subtype_text: optionalString(file.doc_subtype_text),
    ai_diagnosis: file.ai_diagnosis ?? null,
  };
  const categoria = mapProcessFileCategory(normalizedFile);
  const status = mapProcessFileStatus(normalizedFile);
  const tipo = getProcessFileTypeLabel(normalizedFile);
  const nome = getProcessFileNameLabel(normalizedFile, tipo);
  const arquivoNomeOriginal = optionalString(normalizedFile.name) ?? fileNameFromPath(normalizedFile.path);
  const diagnosis = normalizedFile.ai_diagnosis ?? null;

  return {
    id: String(normalizedFile.id),
    nome,
    categoria,
    tipo,
    status,
    confianca: toConfidencePercentage(
      diagnosis?.confianca_extracao,
      diagnosis?.confianca_classificacao,
      normalizedFile.extracted_data?.confidence_score
    ),
    origem: 'Upload',
    dataEnvio: normalizeDateTime(normalizedFile.created_at),
    paginas: 1,
    arquivoNomeOriginal,
    path: normalizedFile.path,
    processId: normalizedFile.process_id === null || normalizedFile.process_id === undefined
      ? null
      : String(normalizedFile.process_id),
    file: normalizedFile,
    campos: mergeExtractedFields(collectExtractedFields(normalizedFile.extracted_data, tipo)),
    motivoRejeicao: status === 'reprovado' ? buildRejectionReason(normalizedFile) : undefined,
    tipoRejeicao: status === 'reprovado' ? 'regra' : undefined,
  };
}

function normalizeFieldSources(value: unknown): Record<string, HabitaDossierFieldSource> {
  const record = objectRecord(value);
  if (!record) return {};

  return Object.entries(record).reduce<Record<string, HabitaDossierFieldSource>>((acc, [key, rawSource]) => {
    const source = objectRecord(rawSource);
    if (!source) return acc;

    acc[key] = {
      source: optionalString(source.source),
      confidence: toNumberOrNull(source.confidence),
      created_at: optionalString(source.created_at),
      created_by: optionalString(source.created_by),
      source_detail: objectRecord(source.source_detail),
    };
    return acc;
  }, {});
}

function normalizeStatus(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function dossierValueOf<T>(field: HabitaDossierField<T> | undefined): T | undefined {
  return field?.value;
}

export function mapProcessDetailToHabitaCliente(
  detail: HabitaProcessDetail,
  files: ProcessFileModel[] | null = null
): HabitaCliente {
  const id = String(detail.id);
  const dossier: HabitaCustomerDossier = detail.customer_dossier ?? {};
  const sourceFiles = Array.isArray(files) ? files : [];

  const fullName = dossierValueOf(dossier.full_name);
  const cpf = dossierValueOf(dossier.cpf);
  const residenceData = dossierValueOf(dossier.residence_data);
  const incomeData = dossierValueOf(dossier.income_data);
  const civilStateData = dossierValueOf(dossier.civil_state_data);
  const primaryIncome = incomeData?.incomes?.[0];
  const parentsValue = dossierValueOf(dossier.parents);
  const parentNames = Array.isArray(parentsValue) ? parentsValue.slice(0, 2) : [];

  const status = apiStatusToPastaStatus(detail.status);
  const createdAt = normalizeDateTime(detail.created_at);
  const updatedAt = normalizeDateTime(detail.updated_at) || createdAt;
  const statusUpdatedAt = normalizeDateTime(detail.last_status_update_at) || updatedAt;

  const dossierFieldsByFileId = collectDossierFieldsByFileId(dossier);
  const documentos = sourceFiles.map((file) => {
    const documento = mapProcessFileToHabitaDocumento(file);
    const dossierFields = dossierFieldsByFileId.get(String(file.id)) ?? [];

    return {
      ...documento,
      campos: mergeExtractedFields(documento.campos, dossierFields),
    };
  });

  const timeline: HabitaCliente['timeline'] = [];
  const usuario = optionalString(detail.created_by) ?? undefined;
  if (createdAt) {
    timeline.push({
      data: createdAt,
      tipo: 'recebido',
      descricao: 'Processo recebido na esteira operacional',
      usuario,
    });
  }
  if (statusUpdatedAt) {
    timeline.push({
      data: statusUpdatedAt,
      tipo: status === 'APROVADO'
        ? 'aprovacao'
        : status === 'REPROVADO'
          ? 'arquivado'
          : status === 'desconhecido'
            ? 'revisao'
            : 'validacao',
      descricao: `Status atual: ${PASTA_STATUS_LABELS[status]}`,
      usuario,
    });
  }

  return {
    id,
    nome: firstString(fullName, `Processo #${id}`),
    nomeCompleto: optionalString(fullName) ?? undefined,
    processoId: `#${id}`,
    cpf: formatCPF(cpf ?? '') || '-',
    email: '-',
    telefone: '-',
    genero: optionalString(dossierValueOf(dossier.gender)),
    pais: parentNames,
    cep: formatCEP(residenceData?.zipcode ?? ''),
    endereco: firstString(residenceData?.full_address, residenceData?.address_line_1),
    cidade: optionalString(residenceData?.city),
    estado: optionalString(residenceData?.state),
    renda: formatHabitaCurrency(toNumberOrNull(primaryIncome?.gross_income ?? incomeData?.avg_monthly_income)),
    rendaLiquida: formatHabitaCurrency(toNumberOrNull(primaryIncome?.net_income)),
    rendaMediaMensal: formatHabitaCurrency(toNumberOrNull(incomeData?.avg_monthly_income)),
    estadoCivil: firstString(civilStateData?.civil_state, civilStateData?.marital_status),
    conjuge: optionalString(civilStateData?.partner_name) ?? undefined,
    conjugeCpf: civilStateData?.partner_cpf ? formatCPF(String(civilStateData.partner_cpf)) : undefined,
    regimeBens: optionalString(civilStateData?.property_regime),
    dependentes: 0,
    parcelaDesejada: formatHabitaCurrency(null),
    imovelDesejado: null,
    nascimento: formatHabitaNullableDate(dossierValueOf(dossier.birth_date)),
    localNascimento: optionalString(dossierValueOf(dossier.birth_place)),
    nacionalidade: optionalString(dossierValueOf(dossier.nationality)),
    profissao: firstString(incomeData?.main_occupation, primaryIncome?.occupation),
    tipoRenda: null,
    temImovel: null,
    crmContactId: optionalString(detail.crm_contact_id),
    chatwootContactId: optionalString(detail.chatwoot_contact_id),
    status,
    responsavel: firstString(detail.responsible, detail.created_by),
    origem: 'Upload',
    criadoEm: createdAt,
    atualizadoEm: updatedAt,
    documentos,
    documentosTotal: documentos.length,
    documentosValidados: documentos.filter((d) => d.status === 'validado').length,
    documentosReprovados: documentos.filter((d) => d.status === 'reprovado').length,
    fieldSources: normalizeFieldSources(dossier),
    timeline,
    pendencias: [],
  };
}
